import React from 'react';
import { BookOpen, Check, AlertCircle, CheckCircle2, Trash2 } from 'lucide-react';
import type { ParsedRow } from './csvImportUtils.js';

interface BulkImportPreviewTableProps {
  parsedRows: ParsedRow[];
  totalValid: number;
  totalErrors: number;
  onReset: () => void;
  onRemoveRow: (index: number) => void;
}

export const BulkImportPreviewTable: React.FC<BulkImportPreviewTableProps> = ({
  parsedRows,
  totalValid,
  totalErrors,
  onReset,
  onRemoveRow,
}) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs sm:text-sm font-bold text-slate-900 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-indigo-500" />
          <span>Review Data Buku Terbaca ({parsedRows.length} baris)</span>
        </h3>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 bg-emerald-50 border border-emerald-100 text-emerald-700 font-semibold rounded-lg flex items-center gap-1">
            <Check className="w-3 h-3" />
            <span>{totalValid} Valid</span>
          </span>
          {totalErrors > 0 && (
            <span className="px-2 py-1 bg-rose-50 border border-rose-100 text-rose-700 font-semibold rounded-lg flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              <span>{totalErrors} Bermasalah</span>
            </span>
          )}
          <button
            type="button"
            onClick={onReset}
            className="px-2 py-1 hover:bg-slate-100 text-slate-500 rounded-lg transition cursor-pointer"
          >
            Reset / Mulai Ulang
          </button>
        </div>
      </div>

      {/* Desktop Preview Table */}
      <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-2xs max-h-80 overflow-y-auto overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 sticky top-0 z-10">
            <tr>
              <th className="p-3">#</th>
              <th className="p-3">Judul Buku</th>
              <th className="p-3">Penulis</th>
              <th className="p-3">Penerbit</th>
              <th className="p-3">Kategori & Tahun</th>
              <th className="p-3">Status Validasi</th>
              <th className="p-3 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {parsedRows.map((row, rIdx) => (
              <tr key={`row-${row.index}-${rIdx}`} className={`hover:bg-slate-50/50 ${!row.isValid ? 'bg-rose-50/20' : ''}`}>
                <td className="p-3 text-slate-500 font-medium">{row.index}</td>
                <td className="p-3">
                  <p className="font-semibold text-slate-900 line-clamp-1">
                    {row.title || <span className="text-rose-500 italic font-normal">[Kosong]</span>}
                  </p>
                  {row.subtitle && <p className="text-[10px] text-slate-500 italic">Sub: {row.subtitle}</p>}
                  {row.originalTitle && <p className="text-[10px] text-slate-400">Asli: {row.originalTitle}</p>}
                </td>
                <td className="p-3 text-slate-600 line-clamp-1">
                  {row.authorName || <span className="text-rose-500 italic font-normal">[Wajib Diisi]</span>}
                  {row.authorWhatsapp && <span className="block text-[10px] text-slate-400 font-mono">WA: {row.authorWhatsapp}</span>}
                </td>
                <td className="p-3 text-slate-600">
                  {row.publisherName || <span className="text-rose-500 italic font-normal">[Wajib Diisi]</span>}
                </td>
                <td className="p-3 text-slate-600">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-medium">{row.category || '?'}</span>
                    <span className="text-[11px] font-semibold">{row.publicationYear || '?'}</span>
                    {row.editionType && row.editionType !== 'orisinal' && (
                      <span className="px-1.5 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[9px] font-medium uppercase">
                        {row.editionType}
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  {row.isValid ? (
                    <span className="text-emerald-600 font-semibold flex items-center gap-1 text-[11px]">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>Siap Impor</span>
                    </span>
                  ) : (
                    <div className="text-rose-600 space-y-0.5 text-[10px] max-w-xs">
                      {row.errors.map((err, idx) => (
                        <div key={`err-${rIdx}-${idx}`} className="flex items-start gap-1 font-medium">
                          <span className="mt-0.5">•</span>
                          <span>{err}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </td>
                <td className="p-3 text-center">
                  <button
                    type="button"
                    onClick={() => onRemoveRow(row.index)}
                    className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-rose-600 transition cursor-pointer"
                    title="Hapus baris ini dari draf"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
